import { Auth } from 'aws-amplify';
import omit from 'lodash/omit';
import getConfig from 'next/config';
import { getAuthDetails, IAuthDetails, isAuthConfigured } from 'utils/authUtils';
import { isBrowser } from 'utils/domUtils';
import { errorMessage } from 'utils/errorUtils';
import { NetworkError } from 'utils/globalTypes';
import { navigateToUrl } from 'utils/navigateUtils';

const { publicRuntimeConfig } = getConfig();
const { NEXT_PUBLIC_DOMAIN, NEXT_PUBLIC_API_URL } = publicRuntimeConfig;
export const investor_api_url = NEXT_PUBLIC_API_URL || 'https://investor.api.' + NEXT_PUBLIC_DOMAIN;

interface IAuthContext {
  authToken?: string;
  groups?: string[];
  approved?: boolean;
  management?: boolean;
  impersonatedUser?: string;
}

const managementEndpoints: { [key: string]: string } = {
  // marketplace
  offeringInfo: `${investor_api_url}/offering/get`,

  // search actions
  statsOfferings: `${investor_api_url}/offering/stats`,
  searchOfferings: `${investor_api_url}/offering/search`,
  mngSearchOfferings: `${investor_api_url}/mng/search_offerings`,
  mngSearchOrders: `${investor_api_url}/mng/search_orders`,
  mngSearchTransactions: `${investor_api_url}/mng/search_transactions`,
  mngSearchPermissions: `${investor_api_url}/mng/search_permissions`,
  mngSearchInvestorFees: `${investor_api_url}/mng/search_investor_fees`,
  mngSearchTrades: `${investor_api_url}/mng/search_trades`,
  mngSearchHoldings: `${investor_api_url}/mng/search_holdings`,
  mngSearchEvents: `${investor_api_url}/mng/search_events`,
  mngSearchDocuments: `${investor_api_url}/mng/search_documents`,

  // dashboard actions
  summary: `${investor_api_url}/mng/summary`,

  // post actions
  listPosts: `${investor_api_url}/post/list`,
  newPost: `${investor_api_url}/post/new`,

  // aml actions
  mngGetAml: `${investor_api_url}/mng/get_aml`,
  mngUpdateAml: `${investor_api_url}/mng/update_aml`,
  mngListDocuments: `${investor_api_url}/mng/list_documents`,
  mngListAmls: `${investor_api_url}/mng/list_amls`,

  // notification actions
  listNotifications: `${investor_api_url}/mng/list_notifications`,
  listAlertNotifications: `${investor_api_url}/mng/list_alert_notifications`,
  readNotification: `${investor_api_url}/mng/read_notification`,
  readAllNotifications: `${investor_api_url}/mng/read_all_notifications`,

  // user email actions
  sendVerifyEmail: `${investor_api_url}/user/send_verify_email`,
  verifyEmail: `${investor_api_url}/user/verify_email`,
  unsubscribeEmail: `${investor_api_url}/user/unsubscribe_email`,

  // user actions
  userGet: `${investor_api_url}/user/get`,
  userUpdate: `${investor_api_url}/user/update`,
  updateUser: `${investor_api_url}/user/update_user`,
  verifyUserName: `${investor_api_url}/user/verify_name`,
  updateUserName: `${investor_api_url}/user/update_name`,
  updateAction: `${investor_api_url}/user/update_action`,
  unregisterUser: `${investor_api_url}/user/unregister`,
  getResource: `${investor_api_url}/user/get_resource`,

  // event actions
  getEventTemplate: `${investor_api_url}/mng/get_event_template`,
  listEventTemplates: `${investor_api_url}/mng/list_event_templates`,
  getEvent: `${investor_api_url}/mng/get_event`,
  createEvent: `${investor_api_url}/mng/create_event`,
  updateEvent: `${investor_api_url}/mng/update_event`,
  deleteEvent: `${investor_api_url}/mng/delete_event`,
  testEvent: `${investor_api_url}/mng/test_event`,
  listEventUsers: `${investor_api_url}/mng/list_event_users`,
  listUserEvents: `${investor_api_url}/mng/list_user_events`,
  sendEvent: `${investor_api_url}/mng/send_event`,

  // offering actions
  listOfferings: `${investor_api_url}/mng/list_offerings`,
  getOffering: `${investor_api_url}/mng/get_offering`,
  updateOffering: `${investor_api_url}/mng/update_offering`,
  reviewOffering: `${investor_api_url}/mng/review_offering`,
  publishOffering: `${investor_api_url}/mng/publish_offering`,
  promoteOffering: `${investor_api_url}/mng/promote_offering`,
  listOfferingVersions: `${investor_api_url}/mng/list_offering_versions`,
  listOfferingsData: `${investor_api_url}/mng/list_offerings_data`,

  // sponsor actions
  listSponsors: `${investor_api_url}/mng/list_sponsors`,
  getSponsor: `${investor_api_url}/mng/get_sponsor`,
  reviewSponsor: `${investor_api_url}/mng/review_sponsor`,
  publishSponsor: `${investor_api_url}/mng/publish_sponsor`,
  promoteSponsor: `${investor_api_url}/mng/promote_sponsor`,
  listSponsorVersions: `${investor_api_url}/mng/list_sponsor_versions`,
  listSponsorsData: `${investor_api_url}/mng/list_sponsors_data`,

  // report actions
  getReport: `${investor_api_url}/mng/get_report`,
  downloadReport: `${investor_api_url}/mng/download_report`,
  listReports: `${investor_api_url}/mng/list_reports`,
  createReport: `${investor_api_url}/mng/create_report`,
  generateReport: `${investor_api_url}/mng/generate_report`,
  updateReport: `${investor_api_url}/mng/update_report`,
  deleteReport: `${investor_api_url}/mng/delete_report`,

  // user actions
  listUsers: `${investor_api_url}/mng/list_users`,
  mngGetUser: `${investor_api_url}/mng/get_user`,
  mngUpdateUser: `${investor_api_url}/mng/update_user`,
  mngUnregisterUser: `${investor_api_url}/mng/unregister_user`,
  mngDeleteUser: `${investor_api_url}/mng/delete_user`,
  mngSendVerifyEmail: `${investor_api_url}/mng/send_verify_email`,
  mngUpdateUserName: `${investor_api_url}/mng/update_user_name`,
  mngInviteUser: `${investor_api_url}/mng/invite_user`,

  // holding actions
  getHolding: `${investor_api_url}/mng/get_holding`,
  listHoldings: `${investor_api_url}/mng/list_holdings`,
  createHolding: `${investor_api_url}/mng/create_holding`,
  updateHolding: `${investor_api_url}/mng/update_holding`,
  deleteHolding: `${investor_api_url}/mng/delete_holding`,

  // transaction actions
  getTransaction: `${investor_api_url}/mng/get_transaction`,
  createTransaction: `${investor_api_url}/mng/create_transaction`,
  updateTransaction: `${investor_api_url}/mng/update_transaction`,
  deleteTransaction: `${investor_api_url}/mng/delete_transaction`,

  // permission actions
  getPermission: `${investor_api_url}/mng/get_permission`,
  createPermission: `${investor_api_url}/mng/create_permission`,
  updatePermission: `${investor_api_url}/mng/update_permission`,
  deletePermission: `${investor_api_url}/mng/delete_permission`,

  // investor fees actions
  createInvestorFee: `${investor_api_url}/mng/create_investor_fee`,
  updateInvestorFee: `${investor_api_url}/mng/update_investor_fee`,
  deleteInvestorFee: `${investor_api_url}/mng/delete_investor_fee`,

  // import actions
  listHoldingsData: `${investor_api_url}/mng/list_holdings_data`,
  getHoldingsData: `${investor_api_url}/mng/get_holdings_data`,
  importHoldingsData: `${investor_api_url}/mng/import_holdings_data`,
  listTransactionsData: `${investor_api_url}/mng/list_transactions_data`,
  getTransactionsData: `${investor_api_url}/mng/get_transactions_data`,
  importTransactionsData: `${investor_api_url}/mng/import_transactions_data`,
  getDriveAuth: `${investor_api_url}/mng/get_drive_auth`,

  // order actions
  getOrder: `${investor_api_url}/mng/get_order`,
  updateOrder: `${investor_api_url}/mng/update_order`,
  listOrders: `${investor_api_url}/mng/list_orders`,
  listOrderActions: `${investor_api_url}/mng/list_order_actions`,
  cancelOrder: `${investor_api_url}/mng/cancel_order`,
  approveOrder: `${investor_api_url}/mng/approve_order`,
  matchBidTrade: `${investor_api_url}/mng/match_bid_manual`,
  matchIntTrade: `${investor_api_url}/mng/match_int_manual`,
  getDocument: `${investor_api_url}/mng/get_document`,

  // trade actions
  cancelTrade: `${investor_api_url}/mng/cancel_trade`,
  executeTrade: `${investor_api_url}/mng/execute_trade_manual`,
  finalizeTrade: `${investor_api_url}/mng/finalize_trade_manual`,
  updateTrade: `${investor_api_url}/mng/update_trade`
};

const approvedEndpoints: { [key: string]: string } = {
  // portfolio
  statsOfferings: `${investor_api_url}/offering/stats`,
  searchOfferings: `${investor_api_url}/offering/search`,
  searchWatch: `${investor_api_url}/watch/search`,
  searchDocuments: `${investor_api_url}/document/search`,
  searchPermissions: `${investor_api_url}/permission/search`,
  searchTransactions: `${investor_api_url}/transaction/search`,
  searchHoldings: `${investor_api_url}/holding/search`,
  searchOrders: `${investor_api_url}/order/search`,

  // dashboard
  summary: `${investor_api_url}/investor/summary`,
  recentActivity: `${investor_api_url}/investor/activity`,

  // watch
  addWatch: `${investor_api_url}/watch/add`,
  removeWatch: `${investor_api_url}/watch/remove`,

  // notifications
  listNotifications: `${investor_api_url}/notification/list`,
  listWatchNotifications: `${investor_api_url}/notification/list_watch`,
  listAlertNotifications: `${investor_api_url}/notification/list_alert`,
  readNotification: `${investor_api_url}/notification/read`,
  readAllNotifications: `${investor_api_url}/notification/read_all`,
  readAllWatchNotifications: `${investor_api_url}/notification/read_watch_all`,

  // posts
  listPosts: `${investor_api_url}/post/list`,
  newPost: `${investor_api_url}/post/new`,

  // users
  getResource: `${investor_api_url}/user/get_resource`,

  // offerings
  listOfferings: `${investor_api_url}/offering/list`,
  expressOfferingInterest: `${investor_api_url}/offering/express_offering_interest`,
  implicitOfferingInterest: `${investor_api_url}/offering/implicit_offering_interest`,
  checkNDAApproval: `${investor_api_url}/offering/check_nda_approval`,
  approveNDA: `${investor_api_url}/offering/approve_nda`,
  accessOffering: `${investor_api_url}/offering/access`,

  // sponsors
  getSponsor: `${investor_api_url}/sponsor/get`,
  listSponsors: `${investor_api_url}/sponsor/list`,

  // orders
  getOrder: `${investor_api_url}/order/get`,
  listOrders: `${investor_api_url}/order/list`,
  startIntBuyOrder: `${investor_api_url}/order/start_int_buy_order`,
  startBidBuyOrder: `${investor_api_url}/order/start_bid_buy_order`,
  startBidSellOrder: `${investor_api_url}/order/start_bid_sell_order`,
  cancelOrder: `${investor_api_url}/order/cancel_order`,
  approveOrder: `${investor_api_url}/order/approve_order`
};

const authEndpoints: { [key: string]: string } = {
  // marketplace
  offeringInfo: `${investor_api_url}/offering/get`,

  // sponsors
  getSponsor: `${investor_api_url}/sponsor/get`,
  listSponsors: `${investor_api_url}/sponsor/list`,

  // offerings
  listOfferings: `${investor_api_url}/offering/list`,
  expressOfferingInterest: `${investor_api_url}/offering/express_offering_interest`,
  implicitOfferingInterest: `${investor_api_url}/offering/implicit_offering_interest`,
  checkNDAApproval: `${investor_api_url}/offering/check_nda_approval`,
  approveNDA: `${investor_api_url}/offering/approve_nda`,

  // aml actions
  listAmls: `${investor_api_url}/aml/list`,
  createAml: `${investor_api_url}/aml/new`,
  updateAml: `${investor_api_url}/aml/update`,
  deleteAml: `${investor_api_url}/aml/delete`,

  // documents
  getDocument: `${investor_api_url}/document/get`,
  listDocuments: `${investor_api_url}/document/list`,
  createDocument: `${investor_api_url}/document/new`,
  deleteDocument: `${investor_api_url}/document/delete`,
  uploadDocument: `${investor_api_url}/document/upload`,

  // user
  userGet: `${investor_api_url}/user/get`,
  userUpdate: `${investor_api_url}/user/update`,
  verifyUserName: `${investor_api_url}/user/verify_name`,
  updateUserName: `${investor_api_url}/user/update_name`,
  updateAction: `${investor_api_url}/user/update_action`,
  unregisterUser: `${investor_api_url}/user/unregister`
};

const guestEndpoints: { [key: string]: string } = {
  // marketplace
  searchOfferings: `${investor_api_url}/offering/gsearch`,

  // sponsors
  getSponsor: `${investor_api_url}/sponsor/get`,
  listSponsors: `${investor_api_url}/sponsor/list`,

  // offerings
  listOfferings: `${investor_api_url}/offering/list`,
  expressOfferingInterest: `${investor_api_url}/offering/express_offering_interest`,
  implicitOfferingInterest: `${investor_api_url}/offering/implicit_offering_interest`,
  checkNDAApproval: `${investor_api_url}/offering/check_nda_approval`,
  approveNDA: `${investor_api_url}/offering/approve_nda`,

  // user
  sendVerifyEmail: `${investor_api_url}/user/send_verify_email`,
  verifyEmail: `${investor_api_url}/user/verify_email`,
  unsubscribeEmail: `${investor_api_url}/user/unsubscribe_email`
};

const getAuthContext = async (authDetails?: IAuthDetails) => {
  try {
    if (authDetails?.authToken == undefined) {
      if (isAuthConfigured()) {
        const session = await Auth.currentSession();
        const authDetails = getAuthDetails(session);
        const authToken = session.getIdToken().getJwtToken();

        return {
          authToken,
          approved: authDetails?.isApproved,
          management: authDetails?.isManagement,
          impersonatedUser: authDetails?.isImpersonated ? authDetails?.email : undefined
        } as IAuthContext;
      }
    }
  } catch (e) {
    // ignore exception
  }

  return {
    authToken: authDetails?.authToken,
    management: authDetails?.isManagement,
    approved: authDetails?.isApproved,
    impersonatedUser: authDetails?.isImpersonated ? authDetails?.email : undefined
  } as IAuthContext;
};

export const paginationRequest = async (request: IPaginationQuery) => {
  const body = {
    offering_id: request.offering_id,
    user_filter: request.user_filter,
    offering_filter: request.offering_filter,
    selected_id: request.selected_id,
    ...request.filter,
    limit: request.limit,
    offset: request.offset,
    query: request.query,
    order_by_field: request.order_by_field,
    order_by_desc: request.order_by_desc
  };

  return safeRunRequest(request.requestCmd, body);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const safeRunRequest = async <T = any>(
  endpoint: string,
  body: Record<string, unknown>,
  authDetails?: IAuthDetails
): Promise<T | Record<string, unknown> | undefined> => {
  return runRequest<T>(endpoint, body, authDetails)
    .then((response) => response)
    .catch((e) => {
      console.warn('safeRunRequest failed:', endpoint, e);
      return {};
    });
};

type BodyRequest = Record<string, unknown>;

interface BodyWithSignal extends BodyRequest {
  signal?: AbortSignal | null;
}

export const navigateToDriveUrl = (url: string) => {
  if (!isBrowser()) return;

  const redirectUrl = new URL(url, window.location.href);

  return runRequest<{ data: { auth_url: string } }>('getDriveAuth', {
    redirect_url: redirectUrl.href
  })
    .then((response) => {
      response?.data?.auth_url && navigateToUrl(response?.data?.auth_url);
    })
    .catch((e) => {
      console.log(e);
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const runRequest = async <T = any>(
  endpoint: string,
  body: BodyWithSignal,
  authDetails?: IAuthDetails
) => {
  const headers = { 'content-type': 'application/json' } as Record<string, string>;
  const authContext = await getAuthContext(authDetails);

  if (authContext.authToken) {
    headers.Authorization = authContext.authToken;
  }

  const urlRequest: string =
    headers.Authorization && authContext?.management && managementEndpoints[endpoint]
      ? managementEndpoints[endpoint]
      : authContext.approved && !authContext?.management && approvedEndpoints[endpoint]
      ? approvedEndpoints[endpoint]
      : headers.Authorization && !authContext?.management && authEndpoints[endpoint]
      ? authEndpoints[endpoint]
      : guestEndpoints[endpoint];

  if (authContext?.impersonatedUser) {
    headers.Impersonate = authContext?.impersonatedUser;
  }

  const fetch = global.fetch;

  const response = await fetch(urlRequest, {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    signal: body?.signal,
    body: JSON.stringify(omit(body, 'signal'))
  });

  if (!response) {
    console.warn('Failure - return empty response', endpoint);
    throw new Error('Request failed');
  }

  const responseBody: Promise<T> = await response.json();
  if (response.status != 200) {
    const message = errorMessage(responseBody);
    console.warn('runRequest not ok:', endpoint, response.status, message);
    throw new NetworkError(response.status, message);
  }

  return responseBody;
};

export interface IQuery {
  selected_id?: string;
  filter?: Record<string, unknown>;
  offset?: number;
  limit: number;
  offering_id?: string;
  user_filter?: string;
  offering_filter?: string;
  order_by_field?: string;
  order_by_desc?: boolean;
  loadMore?: boolean;
  refresh?: boolean;
}

export interface IPaginationQuery {
  requestCmd: string;
  query?: Record<string, unknown>;
  offering_id?: string;
  user_filter?: string;
  offering_filter?: string;
  selected_id?: string;
  limit?: number;
  offset?: number;
  filter?: Record<string, unknown>;
  order_by_field?: string;
  order_by_desc?: boolean;
}

export interface ISearch {
  offeringId?: string;
  userSearchTerm?: string;
}
